import { z } from "zod";
import { albumSchema } from "@libs/catalog-engine-contracts/album";
import { playlistSchema } from "./playlist";
import { artistSchema } from "./artist";

/**
 * Wire-shape schemas for the paginated recommendation feeds consumed by
 * `lissen-app`'s infinite-query hooks (Phase 2.5 of the durable-cache
 * migration).
 *
 * Several of these procedures use `z.custom<Awaited<ReturnType<...>>>` for
 * their `output()` declaration (so the API never validates the wire shape
 * at the procedure boundary). The runtime shape is whatever
 * `getPlaylistsById` / `getAlbumsById` / `RecUtils.getArtists` happen to
 * return, which routinely includes server-derived fields beyond the strict
 * client-shared types in `functions/api/types/`. We therefore use
 * `.passthrough()` on the per-item entity schemas so legitimate extras
 * don't trip the runtime parser, and we use **one-way** drift assertions
 * (router output → schema is a superset) on the consumer side rather
 * than bidirectional. The reverse direction would require declaring every
 * server-internal field in the wire schema, which would defeat the
 * purpose of having a small wire schema.
 */

const playlistItemSchema = playlistSchema.passthrough();
const albumItemSchema = albumSchema.passthrough();
const artistItemSchema = artistSchema.passthrough();

// `for_you` items: discriminated union of (playlist | album).
export const forYouItemWireSchema = z.discriminatedUnion("type", [
  playlistItemSchema.extend({ type: z.literal("playlist") }),
  albumItemSchema.extend({ type: z.literal("album") }),
]);

export const forYouPageWireSchema = z.object({
  data: z.array(forYouItemWireSchema),
  // The For You feed cursor became an OPAQUE base64url string at the finding-9
  // pagination fix (per-source consumed offsets encoded in the payload). The
  // union keeps legacy NUMERIC cursors — pages cached on-device before the fix —
  // valid so they still parse and round-trip (the server decodes them via the
  // old inference). New pages always emit strings.
  endCursor: z.union([z.string(), z.number()]).optional(),
  startCursor: z.union([z.string(), z.number()]).optional(),
});

// `recommendedArtists` items: artistSchema + type:"artist".
export const recommendedArtistItemWireSchema = artistItemSchema.extend({
  type: z.literal("artist"),
});

export const recommendedArtistsPageWireSchema = z.object({
  data: z.array(recommendedArtistItemWireSchema),
  endCursor: z.number().optional(),
  startCursor: z.number().optional(),
});

// `recommendedPlaylists` items: full PlaylistType (no type discriminator).
export const recommendedPlaylistItemWireSchema = playlistItemSchema;

export const recommendedPlaylistsPageWireSchema = z.object({
  data: z.array(recommendedPlaylistItemWireSchema),
  endCursor: z.number().optional(),
  startCursor: z.number().optional(),
});

// `albumsByGenre` items: full AlbumType (no type discriminator).
export const albumsByGenrePageWireSchema = z.object({
  data: z.array(albumItemSchema),
  endCursor: z.number().optional(),
  startCursor: z.number().optional(),
});

// `contentByGenre` items: discriminated union of (playlist | album).
const contentByGenreItemWireSchema = z.discriminatedUnion("type", [
  playlistItemSchema.extend({ type: z.literal("playlist") }),
  albumItemSchema.extend({ type: z.literal("album") }),
]);

export const contentByGenrePageWireSchema = z.object({
  data: z.array(contentByGenreItemWireSchema),
  endCursor: z.number().optional(),
  startCursor: z.number().optional(),
});

// `genres.listPaginated` items.
export const genreItemWireSchema = z.object({
  id: z.string(),
  image: z.string().nullable(),
  name: z.string(),
  primaryColour: z.string(),
  secondaryColour: z.string(),
});

export const genresPageWireSchema = z.object({
  data: z.array(genreItemWireSchema),
  endCursor: z.number().optional(),
  startCursor: z.number().optional(),
});

// `musicSync.getPlaylists` items: external (Spotify/AM) playlist summary.
// Server schema is `.and(z.record(z.string(), z.unknown()))` to allow
// platform-specific extras. We mirror that with `.passthrough()`.
export const externalPlaylistWireSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    totalItems: z.number().nullable(),
    imageUrl: z.string().nullable(),
    songs: z
      .array(
        z
          .object({ isrc: z.string().optional().nullable() })
          .passthrough(),
      )
      .optional(),
  })
  .passthrough();

export const musicSyncPlaylistsPageWireSchema = z.object({
  data: z.array(externalPlaylistWireSchema),
  service: z.string(),
  nextCursor: z.string().optional(),
});
